/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

/**
 * HMR-safe Supabase client singleton
 *
 * During development, Vite's Hot Module Replacement (HMR) re-executes this module
 * on every change, which would create new Supabase clients and exhaust the
 * connection pool. By storing the client on the window object, we ensure only
 * one client instance exists across HMR reloads.
 *
 * This pattern is only needed in development - in production, the module only
 * loads once.
 */

// Extend Window interface for TypeScript
declare global {
  interface Window {
    __SUPABASE_CLIENT__?: SupabaseClient
  }
}

function getSupabaseClient(): SupabaseClient {
  // In development, reuse existing client to prevent connection exhaustion during HMR
  if (import.meta.env.DEV && typeof window !== 'undefined' && window.__SUPABASE_CLIENT__) {
    return window.__SUPABASE_CLIENT__
  }

  const client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      // Persist session in local storage
      persistSession: true,
      // Auto refresh token before expiry
      autoRefreshToken: true,
      // Detect session from URL (for OAuth callbacks)
      detectSessionInUrl: true,
      // Storage key for auth tokens
      storageKey: 'aiontrack-auth',
    },
    db: {
      // Use PostgREST schema (default is public)
      schema: 'public',
    },
    global: {
      headers: {
        // Add custom header for identifying requests from web app
        'X-Client-Info': 'aiontrack-app',
      },
    },
    realtime: {
      // Disable realtime by default for better performance
      // Enable it selectively where needed
      params: {
        eventsPerSecond: 2,
      },
    },
  })

  // Store on window for HMR reuse in development
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    window.__SUPABASE_CLIENT__ = client
  }

  return client
}

export const supabase = getSupabaseClient()
